import { Suspense, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { ErrorBoundary } from 'react-error-boundary';
import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import { Trans, useLingui } from '@lingui/react/macro';
import { CATEGORY_ORDER, useCategoryLabels } from '@/lib/category-labels';
import { Squiggle } from '../primitives';
import { NgoCategoryRow } from './ngo-category-row';
import { NgoFiltersBar } from './ngo-filters';
import { NgoCampaignsModal } from './ngo-campaigns-modal';
import { EMPTY_FILTERS, usePartnerNgos, type NgoFilters } from './use-partner-ngos';
import { NewPathsIllustration } from '@worldcoo/ui/illustrations/static/new-paths';

function DirectoryNotice(props: { title: string; body: string }) {
	return (
		<div className="flex flex-col items-center gap-4 py-16 text-center">
			<NewPathsIllustration className="h-auto w-[230px]" />
			<div className="flex flex-col gap-1">
				<p className="text-xl leading-[1.3] font-medium">{props.title}</p>
				<p className="text-ink/60 text-sm leading-[1.5]">{props.body}</p>
			</div>
		</div>
	);
}

function NoResults() {
	const { t } = useLingui();
	return (
		<DirectoryNotice
			title={t`We couldn't find the NGO you're looking for.`}
			body={t`Check the name is spelled correctly, or widen your search criteria.`}
		/>
	);
}

/**
 * Not `NoResults`: "we couldn't find that NGO" would be a lie when we failed to ask. `getPartnerNgos`
 * throws rather than caching an empty directory for an hour, and this keeps it inside the section.
 */
function DirectoryUnavailable() {
	const { t } = useLingui();
	return <DirectoryNotice title={t`We couldn't load the list of NGOs.`} body={t`Please try again in a few minutes.`} />;
}

/**
 * An NGO appears in each of its categories' rows, on purpose. Filters are local: not worth linking, and
 * `validateSearch` would pull the schema into the never-code-split route module. The open partner is `$ngoId`.
 */
function DirectoryBody() {
	const [filters, setFilters] = useState<NgoFilters>(EMPTY_FILTERS);
	const { ngoId } = useParams({ strict: false });
	const categoryLabels = useCategoryLabels();
	const { ngos, filtered, countryOptions, regionOptions } = usePartnerNgos(filters);

	// The FULL roster, so a shared link opens its sheet whatever the filters.
	const openNgo = ngoId ? ngos.find((ngo) => ngo.id === ngoId) : undefined;

	// Narrows the ROWS, or "Salud" still draws every row its NGOs also work in; an empty row reads as "nobody".
	const rows = CATEGORY_ORDER.filter((category) => !filters.category || category === filters.category)
		.map((category) => ({ category, ngos: filtered.filter((ngo) => ngo.categories.includes(category)) }))
		.filter((row) => row.ngos.length > 0);

	return (
		<>
			<div className="mt-12">
				<NgoFiltersBar
					filters={filters}
					onChange={setFilters}
					countryOptions={countryOptions}
					regionOptions={regionOptions}
				/>
			</div>

			{rows.length === 0 ? (
				<NoResults />
			) : (
				<div className="mt-6 flex flex-col gap-6">
					{rows.map((row) => (
						<NgoCategoryRow
							key={row.category}
							category={row.category}
							label={categoryLabels[row.category]}
							ngos={row.ngos}
							selectedNgoId={openNgo?.id}
							// Alone on the page, the row opens itself.
							openByDefault={!!filters.category}
						/>
					))}
				</div>
			)}

			<NgoCampaignsModal ngo={openNgo} />
		</>
	);
}

/** The static heading renders whatever the roster does. */
export function NgoDirectory() {
	const { t } = useLingui();
	const { reset } = useQueryErrorResetBoundary();

	return (
		<section className="bg-blush">
			<div className="mx-auto max-w-[1440px] px-6 py-16 md:py-[86px] xl:px-[149px]">
				<div className="flex flex-col items-center gap-2 text-center">
					{/* One `Trans`: each language picks the words the flourish carries. */}
					<h2 className="text-3xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
						<Trans>
							Worldcoo's <Squiggle>partner NGOs</Squiggle>
						</Trans>
					</h2>
					<p className="text-base leading-[1.3] font-medium">
						{t`We have a network of more than 1,000 partner NGOs that make real impact possible.`}
					</p>
				</div>

				<ErrorBoundary onReset={reset} fallbackRender={() => <DirectoryUnavailable />}>
					<Suspense fallback={<div className="min-h-[600px]" />}>
						<DirectoryBody />
					</Suspense>
				</ErrorBoundary>
			</div>
		</section>
	);
}
